import { ArrowRightIcon } from '@heroicons/react/20/solid';
import React from 'react';

import { FadeSlideIn } from './Animations';
import { cn } from './cn';
import { Loader } from './Loader';

/**
 * Dark raised surface — matches WidgetCard in dark mode (homepage widgets).
 * Slightly translucent + blurred so the page background bleed-through is visible.
 */
export const raisedPanelDarkClassName = `
  dark:border-border dark:bg-surface-raised/80 dark:backdrop-blur-md
  dark:shadow-md dark:shadow-black/40
`;

/**
 * Raised panel surface for list/table shells (light + dark).
 * Frosted glass in both modes — page background (and any glow) shows through subtly.
 */
export const raisedPanelClassName = `
  border border-border-subtle bg-surface/60 backdrop-blur-xl shadow-sm
  ${raisedPanelDarkClassName}
`;

/** “View all” / header links — strong neutral (WCAG on white), not info-blue. */
export const widgetCardHeaderLinkClassName = `
  inline-flex items-center gap-1
  rounded type-caption
  text-text-secondary hover:text-text-primary hover:underline underline-offset-2
  transition-colors duration-200
  cursor-pointer bg-transparent border-none p-0
  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-2
  focus-visible:ring-offset-focus-ring-offset
`;

/** Ellipsis / overflow menu in widget headers — matches header link neutral. */
export const widgetCardHeaderMenuButtonClassName = `
  rounded-md p-1
  text-text-secondary hover:text-text-primary hover:bg-surface-hover
  cursor-pointer bg-transparent border-none
  transition-colors duration-200
  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-2
  focus-visible:ring-offset-focus-ring-offset
`;

export interface WidgetCardProps {
  title?: string;
  description?: string;
  linkText?: string;
  /** Overrides the default accessible name for the header link (defaults to "{linkText} {title}"). */
  linkAccessibleName?: string;
  onLinkClick?: () => void;
  loading?: boolean;
  loadingMessage?: string;
  emptyMessage?: string;
  className?: string;
  headerActions?: React.ReactNode;
  children?: React.ReactNode;
  /** When true, removes internal padding from the body area */
  flush?: boolean;
  /** Semantic heading level for `title` (default 2 — section under page `h1`). Typography stays `type-h3`. */
  titleHeadingLevel?: 2 | 3 | 4;
}

export const WidgetCard = ({
  title,
  description,
  linkText,
  linkAccessibleName,
  onLinkClick,
  loading,
  loadingMessage,
  emptyMessage,
  className,
  headerActions,
  children,
  flush,
  titleHeadingLevel = 2,
}: WidgetCardProps) => {
  const hasContent = React.Children.count(children) > 0;
  const headerLinkAriaLabel = linkAccessibleName ?? (linkText && title ? `${linkText} ${title}` : undefined);
  const titleClassName = 'type-h3 text-text-primary truncate';

  return (
    <FadeSlideIn
      className={cn(
        'flex flex-col rounded-xl overflow-hidden backdrop-blur-xl',
        'border border-border-subtle bg-surface/60 shadow-sm',
        'dark:backdrop-blur-none',
        raisedPanelDarkClassName,
        className
      )}
      offset={12}
      transition={{ duration: 0.35 }}
    >
      {(title || linkText || headerActions) && (
        <div className="flex items-center justify-between px-5 pt-4 pb-3">
          <div className="flex flex-col gap-1 min-w-0">
            {title &&
              (titleHeadingLevel === 3 ? (
                <h3 className={titleClassName}>{title}</h3>
              ) : titleHeadingLevel === 4 ? (
                <h4 className={titleClassName}>{title}</h4>
              ) : (
                <h2 className={titleClassName}>{title}</h2>
              ))}
            {description && <p className="type-caption text-text-muted truncate">{description}</p>}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {headerActions}
            {linkText && onLinkClick && (
              <button
                type="button"
                onClick={onLinkClick}
                className={widgetCardHeaderLinkClassName}
                aria-label={headerLinkAriaLabel}
              >
                {linkText}
                <ArrowRightIcon className="h-3 w-3" aria-hidden />
              </button>
            )}
          </div>
        </div>
      )}
      <div className={cn('flex-1', !flush && 'px-5 pb-4')}>
        {/* Loading/empty keep horizontal padding even when `flush` — flush only serves edge-to-edge list rows. */}
        {loading ? (
          <div className={cn('flex items-center justify-center py-10', flush && 'px-5')}>
            <Loader label={loadingMessage} size="sm" />
          </div>
        ) : !hasContent && emptyMessage ? (
          <div className={cn('flex items-center justify-center py-10', flush && 'px-5')}>
            <p className="type-body-sm text-text-muted text-center">{emptyMessage}</p>
          </div>
        ) : (
          children
        )}
      </div>
    </FadeSlideIn>
  );
};
